import type { JSX } from 'react';
import { useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';

import type { Theme } from '@/types';
import { getArticle } from '@data/fr/articlesData';
import { useSeo } from '@hooks/useSeo';
import { seoArticlesIndex, seoForArticle } from '@data/seo';
import { useT, useLang, pathFor } from '@/i18n';

import ArticlesIndex from './ArticlesIndex';
import ArticleToc from './ArticleToc';
import { buildToc } from './toc';
import './ArticlePage.css';

interface ArticlePageProps {
  theme: Theme;
  onToggleTheme: () => void;
}

export default function ArticlePage({ theme, onToggleTheme }: ArticlePageProps): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const t = useT();
  const lang = useLang();
  // Les articles complets n'existent qu'en francais : `getArticle` lit toujours
  // le jeu francais, et la route /en/articles/<slug> n'est jamais servie.
  const article = slug !== undefined ? getArticle(slug) : undefined;
  const isIndex = slug === undefined;

  const seo = useMemo(
    () => (isIndex || article === undefined ? seoArticlesIndex(lang) : seoForArticle(article)),
    [isIndex, article, lang],
  );
  useSeo(seo);

  if (slug !== undefined && article === undefined) {
    return <Navigate to={pathFor('/articles', lang)} replace />;
  }

  return (
    <>
      <header className="ArticleTop">
        <Link to={pathFor('/', lang)} className="ArticleTop__logo" aria-label={t.nav.home}>
          <span className="ArticleTop__name">Pierre Séré</span>
        </Link>
        <div className="ArticleTop__right">
          <Link to={`${pathFor('/', lang)}#articles`} className="ArticleTop__link">
            {t.articles.portfolio}
          </Link>
          <a
            href="https://solutions-asterion.com"
            target="_blank"
            rel="noopener noreferrer"
            className="ArticleTop__link ArticleTop__link--accent"
          >
            Asterion ↗
          </a>
          <button
            className="ArticleTop__theme"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? t.nav.toLight : t.nav.toDark}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
        </div>
      </header>

      {isIndex || article === undefined ? (
        <ArticlesIndex />
      ) : (
        <main className="ArticleRead">
          <ArticleToc toc={buildToc(article.body).toc} />
          <article className="ArticlePage__inner">
            <Link to={pathFor('/articles', lang)} className="ArticlePage__back">
              {t.articles.backToIndex}
            </Link>
            <p className="ArticlePage__cat">{article.category}</p>
            <h1 className="ArticlePage__title">{article.title}</h1>
            <p className="ArticlePage__byline">
              {t.articles.by} <strong>Pierre Séré</strong> · {article.readingMin} {t.articles.readingTime}
            </p>

            {/* Contenu maison statique, assaini par DOMPurify avant injection. */}
            <div
              className="ArticlePage__body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(buildToc(article.body).html) }}
            />

            <a
              className="ArticlePage__canonical"
              href={article.canonical}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.articles.readOnAsterion}
            </a>

            <Link to={pathFor('/articles', lang)} className="ArticlePage__back ArticlePage__back--bottom">
              {t.articles.backToIndex}
            </Link>
          </article>
        </main>
      )}

      <footer className="SiteFooter">
        <span className="SiteFooter__text">© 2026 Pierre Séré · pierresere.com</span>
        <span className="SiteFooter__text">{t.footer.builtWith}</span>
      </footer>
    </>
  );
}
